<template>
  <v-container fluid class="px-7 py-6">
    <v-row>
      <v-col cols="12">
        <div class="font-weight-bold grey--text text--darken-1 text-overline">
          {{ somenteLeitura ? 'Verificar evidências' : 'Executar tarefa' }}
        </div>
        <div class="align-center d-flex mt-1">
          <h2 class="font-weight-medium ma-0 text-h6">{{ tituloTratamento }}</h2>
          <v-tooltip v-if="!somenteLeitura" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                aria-label="Como analisar a justificativa"
                class="ml-1 mt-1"
                color="primary"
                icon
                small
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-help-circle-outline</v-icon>
              </v-btn>
            </template>
            <span class="d-inline-block" style="max-width: 340px">
              Avalie a evidência apresentada. Ao aprovar, o erro será encerrado. Ao rejeitar, o erro será reaberto e uma nova
              tarefa de justificativa será criada.
            </span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>

    <v-form class="mt-5" @submit.prevent="solicitarDecisao('APROVAR')">
      <v-row>
        <v-col cols="12">
          <template v-if="correcao">
            <v-alert v-if="validacaoCorrecao && !validacaoCorrecao.possui_erros" dense outlined type="success">
              A correção foi validada com sucesso. Não foram encontrados erros na validação correspondente.
            </v-alert>

            <v-alert v-else-if="validacaoCorrecao && validacaoCorrecao.possui_erros" dense outlined type="info">
              A correção foi executada, porém a validação correspondente ainda identificou erros.
            </v-alert>

            <div class="font-weight-medium grey--text mb-1 mt-1 text--darken-1 text-subtitle-2">Descrição da correção</div>
            <p class="body-1 mb-3" style="white-space: pre-line">{{ correcao.observacao || '—' }}</p>

            <ajustes-selecionados-agrupados class="mt-3" :ajustes="ajustesCorrecao" :erro-id="erro.id" permitir-download />
          </template>

          <template v-if="analise">
            <div class="font-weight-medium grey--text mb-1 text--darken-1 text-subtitle-2">Dados da análise</div>
            <p class="body-1 mb-3" style="white-space: pre-line">{{ analise.descricao || '—' }}</p>

            <template v-if="origemErro">
              <div class="font-weight-medium grey--text mb-1 mt-6 text--darken-1 text-subtitle-2">Origem do erro</div>
              <p class="body-1 font-weight-medium mb-3">{{ origemErro }}</p>
            </template>

            <div class="font-weight-medium grey--text mb-1 mt-6 text--darken-1 text-subtitle-2">Correção aplicada</div>
            <p class="body-1 mb-3" style="white-space: pre-line">{{ analise.correcao_aplicada || '—' }}</p>

            <div class="font-weight-medium grey--text mb-4 mt-6 text--darken-1 text-subtitle-2">
              Evidências {{ analise.justificativas.length ? `(${analise.justificativas.length})` : '' }}
            </div>
            <v-expansion-panels v-if="evidenciasAnaliseAgrupadas.length" accordion flat>
              <v-expansion-panel v-for="grupo in evidenciasAnaliseAgrupadas" :key="grupo.tipo">
                <v-expansion-panel-header class="body-2 font-weight-medium px-0">
                  {{ tituloGrupoEvidenciasAnalise(grupo) }}
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-0">
                  <v-row v-if="grupo.tipo === 'FOTO'">
                    <v-col v-for="foto in fotosGrupoEvidenciasAnalise(grupo)" :key="foto.id" cols="12" md="4">
                      <v-card outlined tile>
                        <v-img v-if="previews[foto.id]" :src="previews[foto.id]" aspect-ratio="1" />
                        <v-responsive v-else aspect-ratio="1" class="align-center d-flex justify-center">
                          <v-skeleton-loader v-if="carregandoPreviews" height="100%" tile type="image" width="100%" />
                          <span v-else class="caption grey--text">Não foi possível carregar a imagem.</span>
                        </v-responsive>
                        <v-card-actions class="px-3 py-2">
                          <span class="caption text-truncate">{{ foto.arquivo }}</span>
                          <v-spacer />
                          <v-btn
                            :aria-label="`Baixar ${foto.arquivo}`"
                            :loading="baixando === `foto-${foto.id}`"
                            color="primary"
                            icon
                            small
                            type="button"
                            @click="baixarImagem(foto)"
                          >
                            <v-icon small>mdi-download</v-icon>
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                  <template v-for="(evidenciaAnalise, indice) in grupo.evidencias" v-else>
                    <div :key="evidenciaAnalise.id" class="align-center body-2 d-flex font-weight-medium px-0 py-3">
                      <span>{{ tituloEvidenciaAnalise(evidenciaAnalise, indice) }}</span>
                      <v-spacer />
                      <v-btn
                        :aria-label="`Baixar evidência ${indice + 1}`"
                        color="primary"
                        icon
                        small
                        type="button"
                        @click="baixarEvidenciaAnalise(evidenciaAnalise)"
                      >
                        <v-icon small>mdi-download</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <span v-else class="caption grey--text">Nenhuma evidência registrada.</span>
          </template>

          <v-alert v-if="tipoJustificativa === 'VALIDACAO_BDGD'" class="mb-0" dense outlined type="info">
            Esta justificativa foi baseada em
            <template v-if="validacoesJustificativa.length > 1">
              {{ validacoesJustificativa.length }} validações BDGD; revise as evidências abaixo.
            </template>
            <template v-else-if="justificativa.validacao_bdgd">
              <a class="primary--text" href="#" @click.prevent="baixarValidacao()">
                validação BDGD #{{ justificativa.validacao_bdgd.id }}
              </a>
              ; clique no link para baixar o resultado.
            </template>
            <span v-else>validação da BDGD.</span>
            <v-progress-circular
              v-if="justificativa.validacao_bdgd && baixando === `validacao-${justificativa.validacao_bdgd.id}`"
              class="ml-2"
              color="primary"
              indeterminate
              size="16"
              width="2"
            />
          </v-alert>

          <v-alert v-if="tipoJustificativa === 'RELATORIO'" class="mb-0" dense outlined type="info">
            Esta justificativa foi baseada em
            <template v-if="relatoriosJustificativa.length > 1">
              {{ relatoriosJustificativa.length }} relatórios; revise as evidências abaixo.
            </template>
            <template v-else-if="justificativa.relatorio">
              <a class="primary--text" href="#" @click.prevent="baixarRelatorio()">relatório {{ nomeRelatorio }}</a>
              ; clique no link para baixar o resultado.
            </template>
            <span v-else>relatório.</span>
            <v-progress-circular
              v-if="justificativa.relatorio && baixando === `relatorio-${justificativa.relatorio.id}`"
              class="ml-2"
              color="primary"
              indeterminate
              size="16"
              width="2"
            />
          </v-alert>

          <v-alert v-if="tipoJustificativa === 'EXTRACAO_BDGD'" class="mb-0" dense outlined type="info">
            Esta justificativa foi baseada em
            <template v-if="extracoesJustificativa.length > 1">
              {{ extracoesJustificativa.length }} extrações BDGD; revise as evidências abaixo.
            </template>
            <template v-else-if="justificativa.extracao_bdgd">
              <a class="primary--text" href="#" @click.prevent="baixarExtracao()">extração BDGD {{ tituloExtracao }}</a>
              ; clique no link para baixar o resultado.
            </template>
            <span v-else>extração BDGD.</span>
            <v-progress-circular
              v-if="justificativa.extracao_bdgd && baixando === `extracao-${justificativa.extracao_bdgd.id}`"
              class="ml-2"
              color="primary"
              indeterminate
              size="16"
              width="2"
            />
          </v-alert>

          <v-alert v-if="tipoJustificativa === 'CONSULTA_SQL'" class="mb-0" dense outlined type="info">
            Esta justificativa foi baseada em uma
            <template v-if="justificativa.consulta_sql">
              <a class="primary--text" href="#" @click.prevent="baixarConsultaSql()">consulta SQL</a>
              ; clique no link para baixar o arquivo.
            </template>
            <template v-else-if="justificativa.arquivos && justificativa.arquivos.length">
              <span>importação de arquivo(s) SQL.</span>
            </template>
            <span v-else>consulta SQL.</span>
          </v-alert>

          <template v-if="justificativa">
            <div class="font-weight-medium grey--text mb-1 mt-6 text--darken-1 text-subtitle-2">Observação da Justificativa</div>
            <p class="body-1 mb-3" style="white-space: pre-line">{{ justificativa.observacao || '—' }}</p>
          </template>

          <v-row v-if="!analise && tipoJustificativa === 'FOTO'">
            <v-col v-for="foto in justificativa.arquivos" :key="foto.id" cols="12" md="4">
              <v-card outlined tile>
                <v-img v-if="previews[foto.id]" :src="previews[foto.id]" aspect-ratio="1" />
                <v-responsive v-else aspect-ratio="1" class="align-center d-flex justify-center">
                  <v-skeleton-loader v-if="carregandoPreviews" height="100%" tile type="image" width="100%" />
                  <span v-else class="caption grey--text">Não foi possível carregar a imagem.</span>
                </v-responsive>
                <v-card-actions class="px-3 py-2">
                  <span class="caption text-truncate">{{ foto.arquivo }}</span>
                  <v-spacer />
                  <v-btn
                    :loading="baixando === `foto-${foto.id}`"
                    color="primary"
                    icon
                    small
                    type="button"
                    @click="baixarImagem(foto)"
                  >
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <evidencias-para-download
            v-if="tipoJustificativa === 'VALIDACAO_BDGD' && validacoesJustificativa.length > 1"
            :evidencias="
              validacoesJustificativa.map((validacao) => ({
                ...validacao,
                chave: `validacao-${validacao.id}`,
                titulo: `Validação BDGD #${validacao.id}`,
                subtitulo: validacao.status || '—'
              }))
            "
            :baixando="baixando"
            icone="mdi-check"
            @baixar="baixarValidacaoSelecionada"
          />

          <evidencias-para-download
            v-if="tipoJustificativa === 'RELATORIO' && relatoriosJustificativa.length > 1"
            :evidencias="
              relatoriosJustificativa.map((relatorio) => ({
                ...relatorio,
                chave: `relatorio-${relatorio.id}`,
                titulo: relatorio.nome
              }))
            "
            :baixando="baixando"
            icone="mdi-file-document-outline"
            @baixar="baixarRelatorioSelecionado"
          />

          <evidencias-para-download
            v-if="tipoJustificativa === 'EXTRACAO_BDGD' && extracoesJustificativa.length > 1"
            :evidencias="
              extracoesJustificativa.map((extracao) => ({
                ...extracao,
                chave: `extracao-${extracao.id}`,
                titulo: extracao.titulo
              }))
            "
            :baixando="baixando"
            icone="mdi-database-export-outline"
            @baixar="baixarExtracaoSelecionada"
          />

          <evidencias-para-download
            v-if="tipoJustificativa === 'CONSULTA_SQL' && !justificativa.consulta_sql && justificativa.arquivos.length"
            :accordion="justificativa.arquivos.length > 1"
            :evidencias="
              justificativa.arquivos.map((arquivo) => ({
                ...arquivo,
                chave: `sql-${arquivo.id}`,
                titulo: arquivo.arquivo
              }))
            "
            :baixando="baixando"
            icone="mdi-file-code-outline"
            @baixar="baixarArquivoSql"
          />

          <template v-if="origemErro && !analise">
            <div class="font-weight-medium grey--text mb-1 mt-6 text--darken-1 text-subtitle-2">
              {{ analise ? 'Origem do erro' : 'Origem do erro corrigida' }}
            </div>
            <p class="body-1 font-weight-medium mb-3">{{ origemErro }}</p>
          </template>
        </v-col>
        <v-col v-if="!somenteLeitura" cols="12" class="pt-0">
          <observacao-tarefa v-model="observacao" :rows="5" />
        </v-col>
        <v-col v-if="!somenteLeitura" cols="12" class="pt-0">
          <v-alert v-if="erroExecucao" class="mb-4" dense outlined type="error">{{ erroExecucao }}</v-alert>

          <div class="d-flex justify-space-between mt-8">
            <v-btn class="mr-3" color="error" :disabled="executando" outlined type="button" @click="solicitarDecisao('REJEITAR')">
              Rejeitar
            </v-btn>
            <botao-confirmar-tarefa label="Aprovar" :disabled="executando" :loading="executando" />
          </div>
        </v-col>
      </v-row>
    </v-form>

    <v-dialog v-if="!somenteLeitura" v-model="dialogConfirmacao" max-width="420" persistent>
      <v-card tile>
        <v-card-title class="text-h6">{{ tituloConfirmacao }}</v-card-title>
        <v-card-text>
          {{ mensagemConfirmacao }}
          <v-alert v-if="erroExecucao" class="mb-0 mt-4" dense outlined type="error">{{ erroExecucao }}</v-alert>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn :disabled="executando" text @click="cancelarDecisao">Cancelar</v-btn>
          <v-btn :color="corConfirmacao" :loading="executando" dark @click="confirmarDecisao">
            {{ rotuloConfirmacao }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import FilesService from '@/services/FilesService.js';
  import GestaoBdgdsErrosTarefasService from '@/services/GestaoBdgdsErrosTarefasService.js';
  import BotaoConfirmarTarefa from './BotaoConfirmarTarefa.vue';
  import AjustesSelecionadosAgrupados from './AjustesSelecionadosAgrupados.vue';
  import EvidenciasParaDownload from './EvidenciasParaDownload.vue';
  import ObservacaoTarefa from './ObservacaoTarefa.vue';
  import { origensErro } from './origensErro.js';

  const titulosJustificativa = {
    FOTO: 'Imagens enviadas como justificativa',
    CONSULTA_SQL: 'Consulta SQL enviada como justificativa',
    VALIDACAO_BDGD: 'Validação da BDGD utilizada como justificativa',
    RELATORIO: 'Relatório utilizado como justificativa',
    EXTRACAO_BDGD: 'Extração BDGD utilizada como justificativa'
  };

  export default {
    name: 'EncerrarErro',

    components: {
      AjustesSelecionadosAgrupados,
      BotaoConfirmarTarefa,
      EvidenciasParaDownload,
      ObservacaoTarefa
    },

    props: {
      erro: {
        type: Object,
        required: true
      },
      tarefa: {
        type: Object,
        required: true
      },
      somenteLeitura: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        previews: {},
        carregandoPreviews: false,
        baixando: '',
        executando: false,
        erroExecucao: '',
        observacao: '',
        dialogConfirmacao: false,
        decisaoPendente: null
      };
    },

    computed: {
      justificativa() {
        return this.erro.justificativa || null;
      },

      analise() {
        return this.erro.analise || null;
      },

      correcao() {
        return this.erro.correcao || null;
      },

      origemErro() {
        if (!this.erro.origem_erro || (!this.analise && !this.erro.origem_corrigida)) return null;

        return origensErro.find((origem) => origem.value === this.erro.origem_erro)?.text || this.erro.origem_erro;
      },

      validacaoCorrecao() {
        if (this.erro.encaminhamento_inicial !== 'CORRIGIR_PRIMEIRO' || !this.correcao) return null;

        return this.correcao.validacao || null;
      },

      ajustesCorrecao() {
        return (this.correcao?.ajustes || []).map((ajuste) => ({
          ...(ajuste.dados || {}),
          chave: `${ajuste.tipo_ajuste}-${ajuste.tabela_ajuste}-${ajuste.registro_ajuste_id}`,
          descricao: ajuste.descricao,
          id: ajuste.registro_ajuste_id,
          tabela_ajuste: ajuste.tabela_ajuste,
          tipo_ajuste: ajuste.tipo_ajuste
        }));
      },

      evidenciasAnaliseAgrupadas() {
        const grupos = new Map();

        (this.analise?.justificativas || []).forEach((evidencia) => {
          const tipo = evidencia.tipo_justificativa || 'OUTRO';
          const grupo = grupos.get(tipo) || { tipo, evidencias: [] };

          grupo.evidencias.push(evidencia);
          grupos.set(tipo, grupo);
        });

        return Array.from(grupos.values());
      },

      tipoTratamento() {
        if (this.analise) return 'a análise';

        return this.correcao ? 'a correção' : 'a justificativa';
      },

      tituloTratamento() {
        if (this.somenteLeitura) {
          if (this.analise) return 'Evidências da análise';

          return this.correcao ? 'Evidências da correção' : 'Evidências da justificativa';
        }

        if (this.analise) return 'Revisar análise';

        return this.correcao ? 'Analisar correção' : 'Analisar justificativa';
      },

      mensagemAjuda() {
        return this.correcao
          ? 'Revise os ajustes registrados. Ao aprovar, o erro será encerrado. Ao rejeitar, o erro será reaberto.'
          : 'Avalie a evidência apresentada. Ao aprovar, o erro será encerrado. Ao rejeitar, o erro será reaberto.';
      },

      tipoJustificativa() {
        return this.justificativa ? this.justificativa.tipo_justificativa : '';
      },

      tituloJustificativa() {
        return titulosJustificativa[this.tipoJustificativa] || 'Justificativa apresentada';
      },

      nomeRelatorio() {
        return this.justificativa && this.justificativa.relatorio ? this.justificativa.relatorio.nome : 'Não informado';
      },

      relatoriosJustificativa() {
        if (this.justificativa?.relatorios?.length) return this.justificativa.relatorios;

        return this.justificativa?.relatorio ? [this.justificativa.relatorio] : [];
      },

      tituloExtracao() {
        return this.justificativa && this.justificativa.extracao_bdgd ? this.justificativa.extracao_bdgd.titulo : 'Não informada';
      },

      extracoesJustificativa() {
        if (this.justificativa?.extracoes_bdgd?.length) return this.justificativa.extracoes_bdgd;

        return this.justificativa?.extracao_bdgd ? [this.justificativa.extracao_bdgd] : [];
      },

      validacoesJustificativa() {
        if (this.justificativa?.validacoes_bdgds?.length) return this.justificativa.validacoes_bdgds;

        return this.justificativa?.validacao_bdgd ? [this.justificativa.validacao_bdgd] : [];
      },

      tituloConfirmacao() {
        return this.decisaoPendente === 'REJEITAR' ? `Rejeitar ${this.tipoTratamento}?` : `Aprovar ${this.tipoTratamento}?`;
      },

      mensagemConfirmacao() {
        return this.decisaoPendente === 'REJEITAR'
          ? 'O erro será reaberto e uma nova justificativa será necessária.'
          : 'O erro será encerrado definitivamente.';
      },

      rotuloConfirmacao() {
        return this.decisaoPendente === 'REJEITAR' ? 'Rejeitar' : 'Aprovar';
      },

      corConfirmacao() {
        return this.decisaoPendente === 'REJEITAR' ? 'error' : 'primary';
      }
    },

    mounted() {
      this.carregarPreviews();
    },

    beforeUnmount() {
      this.limparPreviews();
    },

    methods: {
      rotuloTipoJustificativa(tipo) {
        return titulosJustificativa[tipo] || tipo || 'Justificativa';
      },

      tituloEvidenciaAnalise(evidencia, indice) {
        const prefixo = `Evidência ${indice + 1} — `;

        if (evidencia.tipo_justificativa === 'FOTO') {
          const arquivos = (evidencia.arquivos || []).map((arquivo) => arquivo.arquivo).join(', ');

          return `${prefixo}${arquivos || 'Imagem(s)'}`;
        }

        if (evidencia.tipo_justificativa === 'RELATORIO') {
          return `${prefixo}Relatório utilizado como evidência: ${evidencia.relatorio?.nome || 'não informado'}`;
        }

        if (evidencia.tipo_justificativa === 'EXTRACAO_BDGD') {
          return `${prefixo}Extração BDGD utilizada como evidência: ${evidencia.extracao_bdgd?.titulo || 'não informada'}`;
        }

        if (evidencia.tipo_justificativa === 'VALIDACAO_BDGD') {
          return `${prefixo}Validação BDGD #${evidencia.validacao_bdgd?.id || 'não informada'} utilizada como evidência`;
        }

        return `${prefixo}${this.rotuloTipoJustificativa(evidencia.tipo_justificativa)}`;
      },

      tituloGrupoEvidenciasAnalise(grupo) {
        const rotulos = {
          FOTO: 'Imagens',
          CONSULTA_SQL: 'Consultas SQL',
          VALIDACAO_BDGD: 'Validações BDGD',
          RELATORIO: 'Relatórios',
          EXTRACAO_BDGD: 'Extrações BDGD'
        };

        return `${rotulos[grupo.tipo] || 'Outras evidências'} (${grupo.evidencias.length})`;
      },

      fotosGrupoEvidenciasAnalise(grupo) {
        return grupo.evidencias.reduce((fotos, evidencia) => fotos.concat(evidencia.arquivos || []), []);
      },

      async baixarEvidenciaAnalise(evidencia) {
        if (evidencia.tipo_justificativa === 'RELATORIO') {
          await this.baixarRelatorio(evidencia);
          return;
        }

        if (evidencia.tipo_justificativa === 'EXTRACAO_BDGD') {
          await this.baixarExtracao(evidencia);
          return;
        }

        if (evidencia.tipo_justificativa === 'VALIDACAO_BDGD') {
          await this.baixarValidacao(evidencia);
          return;
        }

        this.baixarConsultaSql(evidencia);
      },

      caminhoImagem(foto) {
        return `imagens/gestao-bdgds-erros/evidencias/imagem-${foto.id}.${foto.extensao}`;
      },

      limparPreviews() {
        Object.values(this.previews).forEach((preview) => URL.revokeObjectURL(preview));
        this.previews = {};
      },

      async carregarPreviews() {
        const fotos = [];

        if (this.tipoJustificativa === 'FOTO' && this.justificativa?.arquivos) {
          fotos.push(...this.justificativa.arquivos);
        }

        (this.analise?.justificativas || []).forEach((evidencia) => {
          if (evidencia.tipo_justificativa === 'FOTO' && evidencia.arquivos) {
            fotos.push(...evidencia.arquivos);
          }
        });

        if (!fotos.length) return;

        this.carregandoPreviews = true;

        try {
          for (const foto of fotos) {
            const { data } = await FilesService.download(foto.id, null, this.caminhoImagem(foto));
            this.$set(this.previews, foto.id, URL.createObjectURL(new Blob([data])));
          }
        } catch (error) {
          this.erroExecucao = 'Não foi possível carregar uma das imagens da justificativa.';
        } finally {
          this.carregandoPreviews = false;
        }
      },

      async baixarArquivo(chave, requisicao, nomeArquivo) {
        this.baixando = chave;
        this.erroExecucao = '';

        try {
          const { data } = await requisicao();
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', nomeArquivo);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          this.erroExecucao = 'Não foi possível baixar a evidência da justificativa.';
        } finally {
          this.baixando = '';
        }
      },

      baixarImagem(foto) {
        return this.baixarArquivo(
          `foto-${foto.id}`,
          () => FilesService.download(foto.id, null, this.caminhoImagem(foto)),
          foto.arquivo
        );
      },

      baixarRelatorio(evidencia = this.justificativa) {
        return this.baixarRelatorioSelecionado(evidencia.relatorio);
      },

      baixarRelatorioSelecionado(relatorio) {
        return this.baixarArquivo(
          `relatorio-${relatorio.id}`,
          () => GestaoBdgdsErrosTarefasService.baixarArquivoEvidencia(this.erro.id, { tipo: 'relatorio', id: relatorio.id }),
          `${relatorio.nome}.xlsx`
        );
      },

      baixarExtracao(evidencia = this.justificativa) {
        return this.baixarExtracaoSelecionada(evidencia.extracao_bdgd);
      },

      baixarExtracaoSelecionada(extracao) {
        return this.baixarArquivo(
          `extracao-${extracao.id}`,
          () =>
            GestaoBdgdsErrosTarefasService.baixarArquivoEvidencia(this.erro.id, { tipo: 'extracao_bdgd', id: extracao.id }),
          `extracao-bdgd-${extracao.id}.zip`
        );
      },

      baixarValidacao(evidencia = this.justificativa) {
        return this.baixarValidacaoSelecionada(evidencia.validacao_bdgd);
      },

      baixarValidacaoSelecionada(validacao) {
        if (!validacao?.id) return;

        return this.baixarArquivo(
          `validacao-${validacao.id}`,
          () =>
            GestaoBdgdsErrosTarefasService.baixarArquivoEvidencia(this.erro.id, { tipo: 'validacao_bdgd', id: validacao.id }),
          `validacao-${this.erro.sigla_teste}-${validacao.id}.xlsx`
        );
      },

      baixarConsultaSql(evidencia = this.justificativa) {
        const conteudo = evidencia && evidencia.consulta_sql;
        if (!conteudo) return;

        const url = window.URL.createObjectURL(new Blob([conteudo], { type: 'text/sql;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `consulta-sql-gestao-bdgd-${this.erro.id}.sql`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },

      baixarArquivoSql(arquivo) {
        return this.baixarArquivo(
          `sql-${arquivo.id}`,
          () => GestaoBdgdsErrosTarefasService.baixarArquivoEvidencia(this.erro.id, { tipo: 'sql', id: arquivo.id }),
          arquivo.arquivo
        );
      },

      solicitarDecisao(decisao) {
        if (this.executando) return;

        this.erroExecucao = '';
        this.decisaoPendente = decisao;
        this.dialogConfirmacao = true;
      },

      cancelarDecisao() {
        this.dialogConfirmacao = false;
        this.decisaoPendente = null;
        this.erroExecucao = '';
      },

      async confirmarDecisao() {
        const executada = await this.decidir(this.decisaoPendente);

        if (executada) this.cancelarDecisao();
      },

      async decidir(decisao) {
        if (this.executando) return;

        this.executando = true;
        this.erroExecucao = '';

        try {
          await GestaoBdgdsErrosTarefasService.encerrarTarefa(this.erro.id, {
            tarefa_id: this.tarefa.id,
            decisao,
            observacao: this.observacao.trim() || null
          });

          this.$emit('executada');
          return true;
        } catch (error) {
          this.erroExecucao =
            (error && error.response && error.response.data && error.response.data.message) ||
            'Não foi possível analisar a justificativa.';
          return false;
        } finally {
          this.executando = false;
        }
      }
    }
  };
</script>

<style scoped>
  .tratamento-cabecalho,
  .ajustes-selecionados {
    border: 1px solid rgba(0, 0, 0, 0.42);
    border-radius: 4px;
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 !important;
  }

  ::v-deep .v-expansion-panel-header {
    padding-top: 0 !important;
  }
</style>
